import React, { useState, useMemo } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Calendar, Download, Printer } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import PropertyPortfolioReport from "../components/reports/PropertyPortfolioReport";
import SalesReport from "../components/reports/SalesReport";
import { base44 } from "@/api/base44Client";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";

export default function ReportsPage() {
  const [activeReport, setActiveReport] = useState("portfolio");
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [dateRange, setDateRange] = useState({
    type: "all",
    startDate: "",
    endDate: "",
  });

  const { data: apartments = [] } = useQuery({
    queryKey: ["apartments"],
    queryFn: () => base44.entities.Apartment.list(),
    initialData: [],
  });

  const { data: sales = [] } = useQuery({
    queryKey: ["sales"],
    queryFn: () => base44.entities.Sale.list(),
    initialData: [],
  });

  const { data: reservations = [] } = useQuery({
  queryKey: ["reservations"],
  queryFn: () => base44.entities.ApartmentReservation.list(),
  initialData: [],
  });

  const { data: clients = [] } = useQuery({
    queryKey: ["clients"],
    queryFn: () => base44.entities.Client.list(),
    initialData: [],
  });

  const { data: payments = [] } = useQuery({
    queryKey: ["payments"],
    queryFn: () => base44.entities.Payment.list(),
    initialData: [],
  });

  const { data: defaultedSales = [] } = useQuery({
    queryKey: ["defaultedSales"],
    queryFn: () => base44.entities.DefaultedSale.list(),
    initialData: [],
  });

  // Filter data based on date range
  const filteredSales = useMemo(() => {
    if (dateRange.type === "all") return sales;

    let start = new Date();
    let end = new Date();

    switch (dateRange.type) {
      case "thisWeek":
        start = new Date();
        start.setDate(start.getDate() - start.getDay());
        start.setHours(0, 0, 0, 0);
        break;
      case "lastWeek":
        start = new Date();
        start.setDate(start.getDate() - start.getDay() - 7);
        start.setHours(0, 0, 0, 0);
        end = new Date(start);
        end.setDate(end.getDate() + 6);
        end.setHours(23, 59, 59, 999);
        break;
      case "thisMonth":
        start = new Date();
        start.setDate(1);
        start.setHours(0, 0, 0, 0);
        break;
      case "lastMonth":
        start = new Date();
        start.setDate(1);
        start.setMonth(start.getMonth() - 1);
        start.setHours(0, 0, 0, 0);
        end = new Date(start);
        end.setDate(1);
        end.setMonth(end.getMonth() + 1);
        end.setDate(0);
        end.setHours(23, 59, 59, 999);
        break;
      case "custom":
        if (dateRange.startDate) {
          start = new Date(dateRange.startDate);
          start.setHours(0, 0, 0, 0);
        }
        if (dateRange.endDate) {
          end = new Date(dateRange.endDate);
          end.setHours(23, 59, 59, 999);
        }
        break;
      default:
        break;
    }

    return sales.filter((sale) => {
      const saleDate = new Date(sale.sale_date);
      return saleDate >= start && saleDate <= end;
    });
  }, [sales, dateRange]);

  const filteredPayments = useMemo(() => {
    if (dateRange.type === "all") return payments;

    let start = new Date();
    let end = new Date();

    switch (dateRange.type) {
      case "thisWeek":
        start = new Date();
        start.setDate(start.getDate() - start.getDay());
        start.setHours(0, 0, 0, 0);
        break;
      case "lastWeek":
        start = new Date();
        start.setDate(start.getDate() - start.getDay() - 7);
        start.setHours(0, 0, 0, 0);
        end = new Date(start);
        end.setDate(end.getDate() + 6);
        end.setHours(23, 59, 59, 999);
        break;
      case "thisMonth":
        start = new Date();
        start.setDate(1);
        start.setHours(0, 0, 0, 0);
        break;
      case "lastMonth":
        start = new Date();
        start.setDate(1);
        start.setMonth(start.getMonth() - 1);
        start.setHours(0, 0, 0, 0);
        end = new Date(start);
        end.setDate(1);
        end.setMonth(end.getMonth() + 1);
        end.setDate(0);
        end.setHours(23, 59, 59, 999);
        break;
      case "custom":
        if (dateRange.startDate) {
          start = new Date(dateRange.startDate);
          start.setHours(0, 0, 0, 0);
        }
        if (dateRange.endDate) {
          end = new Date(dateRange.endDate);
          end.setHours(23, 59, 59, 999);
        }
        break;
      default:
        break;
    }

    return payments.filter((payment) => {
      const paymentDate = new Date(payment.payment_date);
      return paymentDate >= start && paymentDate <= end;
    });
  }, [payments, dateRange]);

  const getFilterLabel = () => {
    switch (dateRange.type) {
      case "all":
        return "All Time";
      case "thisWeek":
        return "This Week";
      case "lastWeek":
        return "Last Week";
      case "thisMonth":
        return "This Month";
      case "lastMonth":
        return "Last Month";
      case "custom":
        if (dateRange.startDate && dateRange.endDate) {
          return `${format(new Date(dateRange.startDate), "MMM d")} - ${format(new Date(dateRange.endDate), "MMM d, yyyy")}`;
        }
        return "Custom Range";
      default:
        return "All Time";
    }
  };

  const setQuickDate = (type) => {
    setDateRange({ type, startDate: "", endDate: "" });
    setIsFilterOpen(false);
  };

  return (
    <div className="space-y-6">
      {/* Header with compact date filter button */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Reports & Analytics</h1>
          <p className="text-slate-600 mt-1">Generate comprehensive reports with custom date ranges</p>
        </div>
        
        <Popover open={isFilterOpen} onOpenChange={setIsFilterOpen}>
          <PopoverTrigger asChild>
            <Button variant="outline" className="gap-2">
              <Calendar className="w-4 h-4" />
              {getFilterLabel()}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-80" align="end">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-xs font-semibold text-slate-500 uppercase">
                  Quick Filters
                </Label>
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    variant={dateRange.type === "all" ? "default" : "outline"}
                    size="sm"
                    onClick={() => setQuickDate("all")}
                    className="text-sm"
                  >
                    All Time
                  </Button>
                  <Button
                    variant={dateRange.type === "thisWeek" ? "default" : "outline"}
                    size="sm"
                    onClick={() => setQuickDate("thisWeek")}
                    className="text-sm"
                  >
                    This Week
                  </Button>
                  <Button
                    variant={dateRange.type === "lastWeek" ? "default" : "outline"}
                    size="sm"
                    onClick={() => setQuickDate("lastWeek")}
                    className="text-sm"
                  >
                    Last Week
                  </Button>
                  <Button
                    variant={dateRange.type === "thisMonth" ? "default" : "outline"}
                    size="sm"
                    onClick={() => setQuickDate("thisMonth")}
                    className="text-sm"
                  >
                    This Month
                  </Button>
                  <Button
                    variant={dateRange.type === "lastMonth" ? "default" : "outline"}
                    size="sm"
                    onClick={() => setQuickDate("lastMonth")}
                    className="text-sm"
                  >
                    Last Month
                  </Button>
                  <Button
                    variant={dateRange.type === "custom" ? "default" : "outline"}
                    size="sm"
                    onClick={() => setDateRange({ ...dateRange, type: "custom" })}
                    className="text-sm"
                  >
                    Custom
                  </Button>
                </div>
              </div>

              {dateRange.type === "custom" && (
                <div className="space-y-3 pt-3 border-t">
                  <div className="space-y-2">
                    <Label htmlFor="startDate">Start Date</Label>
                    <Input
                      id="startDate"
                      type="date"
                      value={dateRange.startDate}
                      onChange={(e) =>
                        setDateRange({ ...dateRange, startDate: e.target.value })
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="endDate">End Date</Label>
                    <Input
                      id="endDate"
                      type="date"
                      value={dateRange.endDate}
                      onChange={(e) =>
                        setDateRange({ ...dateRange, endDate: e.target.value })
                      }
                    />
                  </div>
                </div>
              )}
            </div>
          </PopoverContent>
        </Popover>
      </div>

      {/* Report Type Selection */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card
          className={`cursor-pointer transition-all duration-200 hover:shadow-md ${
            activeReport === "portfolio"
              ? "border-2 border-slate-900 shadow-md"
              : "border border-slate-200"
          }`}
          onClick={() => setActiveReport("portfolio")}
        >
          <CardContent className="p-6 text-center">
            <div className="w-16 h-16 bg-gradient-to-br from-cyan-400 to-cyan-600 rounded-xl flex items-center justify-center mx-auto mb-3">
              <svg
                className="w-8 h-8 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                />
              </svg>
            </div>
            <h3 className="font-semibold text-slate-900 mb-1">
              Property Portfolio
            </h3>
            <p className="text-sm text-slate-600">
              Apartments by status, area, and value
            </p>
          </CardContent>
        </Card>

        <Card
          className={`cursor-pointer transition-all duration-200 hover:shadow-md ${
            activeReport === "sales"
              ? "border-2 border-slate-900 shadow-md"
              : "border border-slate-200"
          }`}
          onClick={() => setActiveReport("sales")}
        >
          <CardContent className="p-6 text-center">
            <div className="w-16 h-16 bg-gradient-to-br from-amber-400 to-amber-600 rounded-xl flex items-center justify-center mx-auto mb-3">
              <svg
                className="w-8 h-8 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <h3 className="font-semibold text-slate-900 mb-1">Sales & Payments Report</h3>
            <p className="text-sm text-slate-600">
              Transaction history and revenue analysis
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Active Report */}
      {activeReport === "portfolio" ? (
        <PropertyPortfolioReport
          apartments={apartments}
          sales={sales}           
          reservations={reservations}
          dateRange={dateRange}
        />
      ) : (
        <SalesReport
          sales={filteredSales}
          unfilteredSales={sales}
          clients={clients}
          apartments={apartments}
          payments={filteredPayments}
          defaultedSales={defaultedSales}
          dateRange={dateRange}
        />
      )}
    </div>
  );
}